import { defineComponent as _defineComponent } from 'vue'
import { computed } from 'vue';

import {
	useConstantStore,
	useFilterStore,
	useLanguageStore,
	useRoutingStore,
	useSearchSuggesterStore,
	useUserStore,
} from '@/helpers/composables/useStores';

import LoginMenuButton from '@/components/nav/LoginMenuButton.vue';
import LoginMenuButtonNew from '@/components/nav/LoginMenuButtonNew.vue';
import SettingsButton from '@/components/nav/static-pages/SettingsButton.vue';
import SearchSuggesterBar from '@/components/search/SearchSuggesterBar.vue';

import { CollectionType } from '@/enums/collection-type';
import { OnboardingStatus } from '@/enums/onboarding-status';
import { isSportSupported } from '@/enums/sports';
import type { FilterCollection } from '@/stores/filter.store';

import { getRouteName, getUrlParamsFromFilterState } from '@/helpers/state-helper';
import { SnowplowContext } from '@/helpers/tracking/providers/snowplow-contexts';
import { TrackingHelper } from '@/helpers/tracking/tracking-helper';
import { isEditorialLocale } from '@/routing/editorial';


export default /*#__PURE__*/_defineComponent({
  __name: 'AppHeader',
  emits: ['tabChange'],
  setup(__props, { emit: emits }) {

const FreemiumFilterBadge = () =>
	import(/* webpackChunkName: "freemium-filter-badge" */ '@/components/filter-bar/FreemiumFilterBadge.vue');


const { subgenresList, providersByShortName, genresByShortName } = useConstantStore();
const { filters } = useFilterStore();
const { country, webLocale } = useLanguageStore();
const { activeRoute, lastRoute, urlMetadataPromise, lastActiveListsCollectionType, lastActiveCollectionType } =
	useRoutingStore();
const { isSearchSuggesterOpen, setSearchSuggesterVisibility } = useSearchSuggesterStore();
const { onboardingStatus, isLoggedIn, isPremium } = useUserStore();

const isNewTab = computed(() => {
	const newPageRoutes = ['app.titles.new', 'app.titles.upcoming', 'app.titles.pricedrops'];
	return newPageRoutes.find(route => (activeRoute.value.name || '').startsWith(route));
});

const isNetflixStatisticPage = computed(() => activeRoute.value.name === 'app.landingpage.statistics');

const isHome = computed(() => activeRoute.value.name === 'app.home');

const isGlobal = computed(() => onboardingStatus.value === OnboardingStatus.GLOBAL);

const isUnsupportedCountry = computed(() => onboardingStatus.value === OnboardingStatus.UNSUPPORTED_COUNTRY);

const isStreamingCharts = computed(() => activeRoute.value.name === 'app.streaming-charts');

const logoSrc = computed(() => {
	const file = `JustWatch-logo-${isGlobal.value ? 'large' : 'small'}`;
	return {
		png: `/${ASSETS_DIR}/img/logo/${file}.png`,
		webp: `/${ASSETS_DIR}/img/logo/${file}.webp`,
	};
});

const hasSports = computed(() => isSportSupported(webLocale.value));

const isValentineList = computed(() => {
	return !!(
		isValentine.value &&
		['US', 'GB', 'CA', 'AU', 'FR', 'DE', 'BR', 'IT', 'TR', 'IN', 'AR', 'ES'].includes(country.value)
	);
});

const valentineListCountryMapping = {
	US: 'tl-tu-3437680c-6645-4fe9-ae45-539c501fb016',
	GB: 'tl-tu-b3acebef-ca21-4314-ad24-7bcd8da6c50b',
	CA: 'tl-tu-44842902-27f3-4a9f-975c-9aafa906238a',
	AU: 'tl-tu-3e635587-6c35-4df1-8578-cdec18a0a3f2',
	FR: 'tl-tu-99220a66-b33d-4d16-bd90-4662a50ea6d6',
	DE: 'tl-tu-1b893f89-0b95-418c-9ee1-3e5acefd5b9c',
	BR: 'tl-tu-4b77ddad-768a-4e23-9c0f-ec15856f82e7',
	IT: 'tl-tu-3fcfc9a0-4916-490b-9ef8-bdc031d08f01',
	TR: 'tl-tu-8d7aa713-1f88-401c-9090-225d3205d5fa',
	IN: 'tl-tu-54933703-0f0c-4f6c-9a4d-15b15bc7ae3e',
	AR: 'tl-tu-870b6894-64af-44ed-a14b-162754d3810f',
	ES: 'tl-tu-6aa065cb-90ab-4001-bac9-ca5aae4acbbc',
};

const valentineListId = valentineListCountryMapping[country.value as keyof typeof valentineListCountryMapping];

const isValentine = computed(() => {
	if (process.client) {
		return !!(new Date().getDate() === 14 && new Date().getMonth() === 1);
	} else {
		return false;
	}
});

const isGuideLocale = computed(() => isEditorialLocale(webLocale.value));

const listsCollectionTypeToNavigate = computed(() => {
	if (lastActiveListsCollectionType.value) {
		return lastActiveListsCollectionType.value;
	}

	return isLoggedIn.value ? CollectionType.MY_LISTS : CollectionType.PUBLIC_LISTS;
});

/**
 * with these parameters we can always link to the correct url for each collectionType page.
 * in some cases we want to overwrite the currentFilters of the passed CollectionType, that's what `filterOverwrite` handles.
 */
function routeParams(collectionType: CollectionType, filterOverwrite?: FilterCollection): any {
	const isNewPageCollectionType = [
		CollectionType.COMING_SOON,
		CollectionType.LEAVING_SOON,
		CollectionType.PRICEDROPS,
	].includes(collectionType);

	const filterCollectionType = isNewPageCollectionType ? CollectionType.NEW : collectionType;
	const _filters = filterOverwrite ?? filters.value(filterCollectionType);
	const routeName = getRouteName(collectionType, _filters, subgenresList.value);
	const params = getUrlParamsFromFilterState(
		routeName,
		_filters,
		providersByShortName.value,
		genresByShortName.value,
		collectionType,
		activeRoute.value.query,
		subgenresList.value
	);

	// Note: list_id query param needs to be deleted when navigating to the Lists tab root level, since we are
	// using it on Lists tab to determinate if list details page is opened
	if (params.query.list_id) {
		delete params.query.list_id;
	}

	return { name: routeName, ...params };
}

async function onTabChange(newTab: string) {
	emits('tabChange');
	await urlMetadataPromise.value;
	const pageTypeContext = await TrackingHelper.getPageTypeContext(lastRoute.value.path, true);
	trackEvent('tabchange', newTab, [pageTypeContext]);
	return newTab;
}

function trackEvent(action: string, label?: string, contexts: SnowplowContext[] = []) {
	TrackingHelper.trackEvent(
		'userinteraction',
		{
			action,
			label,
		},
		contexts
	);

	//TITLE_DETAIL_REDESIGN
	// trackTitleDetailRedesignNewBuyBoxExp({ action: 'click', label: action, property: label }, action !== 'click_logo');
	//TITLE_DETAIL_REDESIGN
}

function onOverlayClick() {
	setSearchSuggesterVisibility(false);
}

return { __sfc: true,FreemiumFilterBadge, emits, subgenresList, providersByShortName, genresByShortName, filters, country, webLocale, activeRoute, lastRoute, urlMetadataPromise, lastActiveListsCollectionType, lastActiveCollectionType, isSearchSuggesterOpen, setSearchSuggesterVisibility, onboardingStatus, isLoggedIn, isPremium, isNewTab, isNetflixStatisticPage, isHome, isGlobal, isUnsupportedCountry, isStreamingCharts, logoSrc, hasSports, isValentineList, valentineListCountryMapping, valentineListId, isValentine, isGuideLocale, listsCollectionTypeToNavigate, routeParams, onTabChange, trackEvent, onOverlayClick, LoginMenuButton, LoginMenuButtonNew, SettingsButton, SearchSuggesterBar, CollectionType }
}

})